import {
  buildGraphFromTraversalAndSearch,
  CollapsedGraphReference,
  collapseGraph,
  GraphInterface,
  LoadedGraphWithViewpointMode,
  RawGraphNode2,
  TraversalAndGroupingResult,
} from '@ardoq/graph';
import { getScenarioGroup } from '../graphViews/traversalViewModel';
import { addScenarioRelatedComponents } from '../relationshipDiagrams/viewModel$/utils';
import { isTruthy } from '@ardoq/common-helpers';
import {
  GraphModelShape,
  LoadedScenarioData,
  ShowRelatedComponentsShape,
} from '@ardoq/data-model';
import { GroupingRule, ViewIds } from '@ardoq/api-types';
import type { I18nLocale } from '@ardoq/i18n';

type GetRawGraphWithScenarioDataArgs = {
  viewInterface: GraphInterface;
  loadedScenarioData: LoadedScenarioData;
  scenarioRelatedComponents: ShowRelatedComponentsShape;
  showScopeRelated: boolean;
  graphModel: GraphModelShape;
  viewId: ViewIds;
  loadedGraph: LoadedGraphWithViewpointMode;
  isInScopeDiffMode: boolean;
  collapsedGroupIds: Set<string>;
  shouldCollapseGraph: boolean;
  userDefinedGroupingRules: GroupingRule[];
  i18nLocale: I18nLocale;
};

type GetRawGraphWithScenarioDataResult = TraversalAndGroupingResult & {
  componentIdsOfCollapsedGroups: string[];
  referenceList: CollapsedGraphReference[];
  uncollapsedNodes: {
    componentMap: Map<string, RawGraphNode2>;
    groupMap: Map<string, RawGraphNode2>;
  };
};
/**
 * Builds a raw graph from traversal/search data, applies scenario-related components,
 * and optionally collapses graph based on collapsed group IDs. Returns both collapsed
 * and uncollapsed node maps. Uncollapsed maps are needed for reference aggregation.
 */
export const getRawGraphWithScenarioData = ({
  viewInterface,
  loadedScenarioData,
  scenarioRelatedComponents,
  showScopeRelated,
  graphModel,
  viewId,
  loadedGraph,
  isInScopeDiffMode,
  collapsedGroupIds,
  shouldCollapseGraph,
  userDefinedGroupingRules,
  i18nLocale,
}: GetRawGraphWithScenarioDataArgs): GetRawGraphWithScenarioDataResult => {
  const scenarioGroup = getScenarioGroup({
    loadedScenarioData,
    showScopeRelated,
    scenarioRelatedComponentIds: scenarioRelatedComponents.componentIds,
  });

  const groupingRules = [scenarioGroup, ...userDefinedGroupingRules].filter(
    isTruthy
  );

  const rawGraph = buildGraphFromTraversalAndSearch({
    graphInterface: viewInterface,
    graph: graphModel,
    groupingRules,
    viewId,
    loadedGraph,
    i18nLocale,
  });

  // this modifies the contents of reference and component maps
  addScenarioRelatedComponents({
    loadedScenarioData,
    showScopeRelated,
    isInScopeDiffMode,
    scenarioRelatedComponentIds: scenarioRelatedComponents.componentIds,
    componentMap: rawGraph.componentMap,
    referenceMap: new Map(rawGraph.referenceList.map(ref => [ref.id, ref])),
    graphModel,
  });

  return shouldCollapseGraph
    ? {
        ...rawGraph,
        ...collapseGraph({
          ...rawGraph,
          collapsedGroupIds,
        }),
        uncollapsedNodes: {
          componentMap: rawGraph.componentMap,
          groupMap: rawGraph.groupMap,
        },
      }
    : {
        ...rawGraph,
        referenceList: rawGraph.referenceList.map(reference => ({
          ...reference,
          uncollapsedSourceId: reference.sourceId,
          uncollapsedTargetId: reference.targetId,
        })),
        componentIdsOfCollapsedGroups: [],
        uncollapsedNodes: {
          componentMap: rawGraph.componentMap,
          groupMap: rawGraph.groupMap,
        },
      };
};
